import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Copy, FileText, Paperclip, SendHorizontal, X } from "lucide-react";

import referralsIcon from "../../../assets/saymon-ui/runtime/icon-referrals.png";
import indicatorIcon from "../../../assets/saymon-ui/runtime/icon-indicator.png";
import analyticsIcon from "../../../assets/saymon-ui/runtime/icon-analytics.png";
import learningIcon from "../../../assets/saymon-ui/runtime/icon-learning.png";
import supportIcon from "../../../assets/saymon-ui/runtime/icon-support.png";
import exclusiveIcon from "../../../assets/saymon-ui/runtime/icon-exclusive.png";
import uidIcon from "../../../assets/saymon-ui/runtime/icon-uid.png";
import refCheckIcon from "../../../assets/saymon-ui/runtime/icon-ref-check.png";
import promoExclusiveIcon from "../../../assets/saymon-ui/runtime/icon-promo-exclusive.png";
import promoCodeIcon from "../../../assets/saymon-ui/runtime/icon-promo-code.png";
import autoDiscountIcon from "../../../assets/saymon-ui/runtime/icon-auto-discount.png";
import cryptomusIcon from "../../../assets/saymon-ui/runtime/icon-cryptomus.png";
import tonIcon from "../../../assets/saymon-ui/runtime/icon-ton.png";
import walletIcon from "../../../assets/saymon-ui/runtime/icon-wallet.png";
import leaderRobot from "../../../assets/saymon-ui/runtime/robot.png";
import saymonDashboard from "../../../assets/saymon-ui/runtime/saymon-dashboard.png";
import saymonPoint from "../../../assets/saymon-ui/runtime/saymon-point.png";
import navHomeIdle from "../../../assets/saymon-ui/nav-states/nav-home-idle.png";
import navHomeSelected from "../../../assets/saymon-ui/nav-states/nav-home-selected.png";
import navLeadersIdle from "../../../assets/saymon-ui/nav-states/nav-leaders-idle.png";
import navLeadersSelected from "../../../assets/saymon-ui/nav-states/nav-leaders-selected.png";
import navProfileIdle from "../../../assets/saymon-ui/nav-states/nav-profile-idle.png";
import navProfileSelected from "../../../assets/saymon-ui/nav-states/nav-profile-selected.png";
import navStatsIdle from "../../../assets/saymon-ui/nav-states/nav-stats-idle.png";
import navStatsSelected from "../../../assets/saymon-ui/nav-states/nav-stats-selected.png";

import { checkPromoCode, createMiniAppManualPayment, createMiniAppPayment, getMiniAppManualQuote, getSupportMessages, getTopUsers, getUserActiveSubscriptions, getUserBalance, getUserLiderboardByTg, getUserProfile, getUserRefLink, getUserStats, sendSupportMessage, SupportMessage, updateTradingViewName, userWithdraw } from "../../../services/users-req";
import { getMiniAppDiscountsReq, getTariffsReq, MiniAppDiscounts } from "../../../services/tariffs-req";
import { formatNumberWithSpace } from "../../../utils/number-transform";
import { IUser, IUserActiveSubscription, IUserProfile, IUserStats } from "../../../types/user-interface";
import { ITariff } from "../../../types/requests-interfaces";
import LoadingPage from "../../general/loading-page.comonent";
import {
  Avatar,
  AnimatedReveal,
  BadgeRow,
  BalanceErrorText,
  BalanceCard,
  BottomNav,
  Card,
  Chip,
  Chips,
  COLORS,
  CTAHint,
  DataLoadingOverlay,
  DesignPage,
  DesignStatCard,
  Donut,
  DurationButton,
  DurationGrid,
  ErrorLine,
  GhostButton,
  GlowButton,
  H1,
  HexAvatar,
  Icon,
  Input,
  Lead,
  LeaderBoard,
  LeaderHero,
  LeaderList,
  LeaderRow,
  LeaderTableRow,
  LeaderTop,
  MetricCard,
  MetricGrid,
  MiniAppGlobalStyle,
  NavIcon,
  NavItem,
  OrderRobot,
  Page,
  Pill,
  Price,
  PrimaryButton,
  ProfileReferralRobot,
  ProductTile,
  ProductsGrid,
  ProgressFill,
  ProgressTrack,
  CopyNotice,
  ReferralCard,
  RobotDecor,
  RowCard,
  ScreenStage,
  SectionTitle,
  Shell,
  SmallText,
  StatsPanel,
  Summary,
  SummaryRow,
  SupportBubble,
  SupportAttachButton,
  SupportAttachment,
  SupportChat,
  SupportComposer,
  SupportMessages,
  SupportMedia,
  SupportSendButton,
  SupportTextarea,
  TitleText,
} from "./mini-app.styles";

const MINI_APP_ASSETS = [
  referralsIcon,
  indicatorIcon,
  analyticsIcon,
  learningIcon,
  supportIcon,
  exclusiveIcon,
  uidIcon,
  refCheckIcon,
  promoExclusiveIcon,
  promoCodeIcon,
  autoDiscountIcon,
  cryptomusIcon,
  tonIcon,
  walletIcon,
  leaderRobot,
  saymonDashboard,
  saymonPoint,
  navHomeIdle,
  navHomeSelected,
  navLeadersIdle,
  navLeadersSelected,
  navProfileIdle,
  navProfileSelected,
  navStatsIdle,
  navStatsSelected,
];

const preloadImage = (src: string) =>
  new Promise<void>((resolve) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = src;
  });

const preloadMiniAppAssets = () =>
  Promise.race([
    Promise.all(Array.from(new Set(MINI_APP_ASSETS)).map(preloadImage)).then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, 5000)),
  ]);

type Screen =
  | "home"
  | "catalog"
  | "config"
  | "checkout"
  | "waiting"
  | "paid"
  | "manual"
  | "manual-payeer"
  | "manual-submitted"
  | "profile"
  | "subscriptions"
  | "nickname"
  | "free"
  | "free-success"
  | "leaders"
  | "stats"
  | "support";

type ProductKind = "subscription" | "study" | "support";

type Product = {
  id: string;
  title: string;
  short: string;
  description: string;
  price: number;
  days: number;
  kind: ProductKind;
  icon: string;
  tone: "yellow" | "blue" | "green" | "purple";
};

const fallbackProducts: Product[] = [
  {
    id: "indicator",
    title: "Индикатор",
    short: "Сигналы и закрытый канал",
    description: "120 USDT за 30 дней. Доступ к каналу, сигналам и инструкции.",
    price: 120,
    days: 30,
    kind: "subscription",
    icon: indicatorIcon,
    tone: "yellow",
  },
  {
    id: "analytics",
    title: "Аналитика",
    short: "Ежедневные обзоры рынка",
    description: "150 USDT за 30 дней. Ежедневная аналитика крипторынка.",
    price: 150,
    days: 30,
    kind: "subscription",
    icon: analyticsIcon,
    tone: "blue",
  },
  {
    id: "learning",
    title: "Обучение",
    short: "Материалы и группа",
    description: "Материалы, записи и приватная группа.",
    price: 300,
    days: 0,
    kind: "study",
    icon: learningIcon,
    tone: "green",
  },
  {
    id: "support",
    title: "Поддержка",
    short: "Вопрос оператору",
    description: "Связаться с оператором поддержки.",
    price: 0,
    days: 30,
    kind: "support",
    icon: supportIcon,
    tone: "purple",
  },
];

const defaultDiscounts: MiniAppDiscounts = {
  duration: { "30": 0, "60": 5, "90": 10, "180": 20, "360": 25 },
  volume: { "2": 10, "3": 17 },
  extra_per_subscription: 2,
};

const stripProductEmoji = (value: string) => value.replace(/^[^\p{L}\p{N}]+/u, "").trim();
const normalizeProductName = (value: string) => stripProductEmoji(value).toLocaleLowerCase("ru-RU");

const tariffToProduct = (tariff: ITariff, index: number): Product => {
  const fields = tariff.button.fields || ({} as ITariff["button"]["fields"]);
  const kind = fields.kind === "study" ? "study" : "subscription";
  const fallbackPool = fallbackProducts.filter((product) => product.kind === kind);
  const fallback = fallbackPool[index % fallbackPool.length] || fallbackProducts[0];
  const title = stripProductEmoji(tariff.button.name || fallback.title);
  const icon = fields.mini_app_icon || fallback.icon;
  const short = fields.mini_app_short_description || fallback.short;
  const days = kind === "study" ? 0 : (Number(fields.date) || 30);
  const description = fields.mini_app_short_description || (
    kind === "study"
      ? `${Number(fields.price) || 0} USDT. ${short}`
      : `${Number(fields.price) || 0} USDT за ${days} дней. ${short}`
  );
  return {
    id: String(tariff.id),
    title,
    short,
    description,
    price: Number(fields.price) || 0,
    days,
    kind,
    icon,
    tone: fallback.tone,
  };
};

const getDurationOptions = (discounts: MiniAppDiscounts) =>
  [30, 60, 90, 180, 360].map((days) => ({ days, discount: Number(discounts.duration[String(days)] || 0) }));

const exchangeLinks: Record<string, string> = {
  BingX: "https://bingx.com/",
  Bitget: "https://www.bitget.com/",
  OKX: "https://www.okx.com/",
};

const apiUrl = process.env.REACT_APP_API_URL;

const getUserId = () => {
  const queryUserId = new URLSearchParams(window.location.search).get("user_id");
  const telegramUserId = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString();
  const savedUserId = localStorage.getItem("userId");
  const id = Number(queryUserId || telegramUserId || savedUserId || 0);
  return Number.isFinite(id) ? id : 0;
};

const money = (value: number) => `${formatNumberWithSpace(Math.round(value))} USDT`;

const shareReferralLink = (url: string) => {
  if (!url) return;
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
  const webApp = (window as any).Telegram?.WebApp;
  if (webApp?.openTelegramLink) {
    webApp.openTelegramLink(shareUrl);
    return;
  }
  window.open(shareUrl, "_blank", "noopener,noreferrer");
};

const openBotCheckout = (selectedProducts: Product[], duration: number, promoCode = "") => {
  const tariffIds = selectedProducts.map((product) => product.id).filter((id) => /^\d+$/.test(id));
  if (!tariffIds.length) return;
  const promoSuffix = promoCode.trim() ? `_${encodeURIComponent(promoCode.trim())}` : "";
  const url = `https://t.me/saimonnnbot?start=miniapp_${tariffIds.join("-")}_${duration}${promoSuffix}`;
  try {
    const webApp = (window as any).Telegram?.WebApp;
    if (webApp?.openTelegramLink) {
      webApp.openTelegramLink(url);
      return;
    }
  } catch {
    // Browser fallback below.
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

const numberOrZero = (value: unknown) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

const withTimeout = <T,>(promise: Promise<T>, timeoutMs = 8000): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => window.setTimeout(() => reject(new Error("Request timeout")), timeoutMs)),
  ]);

const leaderName = (user?: IUser | null) =>
  user?.full_name || user?.username || "Участник";

const leaderPosition = (user?: IUser | null, fallback = 0) => {
  const position = numberOrZero(user?.position);
  return position > 0 ? position : fallback;
};

const volumeDiscount = (count: number, discounts: MiniAppDiscounts) => {
  if (count < 2) return 0;
  return Number(discounts.volume[String(count)] ?? discounts.volume["3"] ?? 0) + Math.max(0, count - 3) * Number(discounts.extra_per_subscription || 0);
};

let routeTransitionUntil = 0;

const useScreen = (): [Screen, (screen: Screen) => void, boolean] => {
  const location = useLocation();
  const navigate = useNavigate();
  const [transitionLoading, setTransitionLoading] = useState(() => Date.now() < routeTransitionUntil);
  const transitionTimer = useRef<number | null>(null);
  const navigationTimer = useRef<number | null>(null);

  const screen = useMemo<Screen>(() => {
    if (location.pathname.includes("catalog")) return "catalog";
    if (location.pathname.includes("configure")) return "config";
    if (location.pathname.includes("checkout")) return "checkout";
    if (location.pathname.includes("payment-waiting")) return "waiting";
    if (location.pathname.includes("payment-success")) return "paid";
    if (location.pathname.includes("manual-payment/payeer")) return "manual-payeer";
    if (location.pathname.includes("manual-payment/submitted")) return "manual-submitted";
    if (location.pathname.includes("manual-payment")) return "manual";
    if (location.pathname.includes("subscriptions")) return "subscriptions";
    if (location.pathname.includes("nickname")) return "nickname";
    if (location.pathname.includes("free-channel/success")) return "free-success";
    if (location.pathname.includes("free-channel")) return "free";
    if (location.pathname.includes("support")) return "support";
    if (location.pathname.includes("liders")) return "leaders";
    if (location.pathname.includes("stats")) return "stats";
    if (location.pathname.includes("profile")) return "profile";
    return "home";
  }, [location.pathname]);

  const setScreen = (next: Screen) => {
    const map: Record<Screen, string> = {
      home: "/home",
      catalog: "/catalog",
      config: "/configure",
      checkout: "/checkout",
      waiting: "/payment-waiting",
      paid: "/payment-success",
      manual: "/manual-payment",
      "manual-payeer": "/manual-payment/payeer",
      "manual-submitted": "/manual-payment/submitted",
      profile: "/profile",
      subscriptions: "/profile/subscriptions",
      nickname: "/profile/nickname",
      free: "/free-channel",
      "free-success": "/free-channel/success",
      leaders: "/liders",
      stats: "/stats",
      support: "/support",
    };
    if (next === screen) return;

    if (navigationTimer.current !== null) window.clearTimeout(navigationTimer.current);
    if (transitionTimer.current !== null) window.clearTimeout(transitionTimer.current);
    routeTransitionUntil = Date.now() + 520;
    setTransitionLoading(true);
    navigate(map[next]);
  };

  useEffect(() => {
    const remaining = routeTransitionUntil - Date.now();
    if (remaining > 0) {
      setTransitionLoading(true);
      transitionTimer.current = window.setTimeout(() => {
        setTransitionLoading(false);
        transitionTimer.current = null;
      }, remaining);
    }

    return () => {
      if (navigationTimer.current !== null) {
        window.clearTimeout(navigationTimer.current);
        navigationTimer.current = null;
      }
      if (transitionTimer.current !== null) {
        window.clearTimeout(transitionTimer.current);
        transitionTimer.current = null;
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    const parentMap: Partial<Record<Screen, Screen>> = {
      catalog: "home",
      config: "catalog",
      checkout: "config",
      waiting: "checkout",
      paid: "checkout",
      manual: "checkout",
      "manual-payeer": "manual",
      "manual-submitted": "manual-payeer",
      subscriptions: "profile",
      nickname: "profile",
      free: "home",
      support: "home",
      "free-success": "free",
    };
    const parent = parentMap[screen];
    const backButton = (window as any).Telegram?.WebApp?.BackButton;

    if (!backButton) return;
    if (!parent) {
      backButton.hide?.();
      return;
    }

    backButton.show?.();
    const handleBack = () => setScreen(parent);
    backButton.offClick?.(handleBack);
    backButton.onClick?.(handleBack);

    return () => {
      backButton.offClick?.(handleBack);
    };
  }, [screen]);

  return [screen, setScreen, transitionLoading];
};

const getGlowStep = (screen: Screen) => {
  const map: Record<Screen, number> = {
    home: 0,
    catalog: 1,
    config: 2,
    checkout: 3,
    waiting: 4,
    paid: 5,
    manual: 6,
    "manual-payeer": 15,
    "manual-submitted": 16,
    free: 7,
    "free-success": 8,
    leaders: 9,
    profile: 10,
    subscriptions: 11,
    nickname: 12,
    stats: 13,
    support: 14,
  };
  return map[screen];
};

function Navigation({ active, setScreen }: { active: Screen; setScreen: (screen: Screen) => void }) {
  const items = [
    ["home", "Домой", navHomeIdle, navHomeSelected],
    ["leaders", "Лидеры", navLeadersIdle, navLeadersSelected],
    ["profile", "Профиль", navProfileIdle, navProfileSelected],
    ["stats", "Статы", navStatsIdle, navStatsSelected],
  ] as const;

  return (
    <BottomNav>
      {items.map(([screen, label, idleIcon, selectedIcon]) => {
        const selected = active === screen ||
          (screen === "home" && active === "support") ||
          (screen === "profile" && ["profile", "subscriptions", "nickname"].includes(active));
        return (
          <NavItem key={screen} $active={selected} onClick={() => setScreen(screen)}>
            <NavIcon src={selected ? selectedIcon : idleIcon} $active={selected} />
            {label}
          </NavItem>
        );
      })}
    </BottomNav>
  );
}

function Dashboard({ balance, products, setSelectedIds, setCatalogFilter, setScreen }: { balance: number; products: Product[]; setSelectedIds: (ids: string[]) => void; setCatalogFilter: (filter: "subscription" | "study") => void; setScreen: (screen: Screen) => void }) {
  const robotRef = useRef<HTMLImageElement | null>(null);
  const [robotVisible, setRobotVisible] = useState(true);

  useEffect(() => {
    const robot = robotRef.current;
    if (!robot) return;

    if (!window.matchMedia("(max-width: 640px)").matches || !("IntersectionObserver" in window)) {
      setRobotVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRobotVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px" },
    );

    observer.observe(robot);
    return () => observer.disconnect();
  }, []);

  return (
    <Page $home>
      <H1>Саймон</H1>
      <Lead>Мини-апп вместо цепочки сообщений: быстрый доступ к продуктам, заказам, профилю и поддержке.</Lead>
      <BalanceCard>
        <Icon src={referralsIcon} />
        <SmallText>Реф. баланс</SmallText>
        <div style={{ marginTop: 4, fontFamily: "Unbounded", fontSize: 18, fontWeight: 800 }}>{formatNumberWithSpace(balance)} $</div>
        <CTAHint>
          <b>Нажми и успей вступить</b>
          в эксклюзивный бесплатный канал с уникальным контентом
        </CTAHint>
        <GlowButton style={{ position: "absolute", right: 16, bottom: 17, width: 134 }} onClick={() => setScreen("free")}>
          Бесплатный канал
        </GlowButton>
      </BalanceCard>
      <SectionTitle onClick={() => setScreen("catalog")} role="button" tabIndex={0}>
        Продукты <span style={{ fontSize: 22 }}>›</span>
      </SectionTitle>
      <ProductsGrid>
        {[...products.slice(0, 3), fallbackProducts[3]].map((product) => (
          <ProductTile key={product.id} onClick={() => {
            if (product.kind === "support") {
              setScreen("support");
              return;
            }
            setSelectedIds([product.id]);
            setCatalogFilter(product.kind);
            setScreen("catalog");
          }}>
            <Icon src={product.icon} />
            <TitleText style={{ marginTop: 8 }}>{product.title}</TitleText>
            <Price $tone={product.tone}>{product.price ? money(product.price) : "24/7"}</Price>
            <SmallText>{product.short}</SmallText>
          </ProductTile>
        ))}
      </ProductsGrid>
      <RobotDecor ref={robotRef} className={robotVisible ? "robot-visible" : undefined} src={saymonDashboard} />
    </Page>
  );
}

function SupportScreen() {
  const userId = getUserId();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [error, setError] = useState("");
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastMessageIdRef = useRef(0);

  useEffect(() => {
    let active = true;
    const loadMessages = async () => {
      try {
        const data = await getSupportMessages(userId, lastMessageIdRef.current);
        if (active) {
          if (data.length) {
            lastMessageIdRef.current = data[data.length - 1].id;
            setMessages((current) => {
              const known = new Set(current.map((message) => message.id));
              return [...current, ...data.filter((message) => !known.has(message.id))];
            });
          }
          setError("");
        }
      } catch {
        if (active) setError("Не удалось загрузить историю переписки");
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadMessages();
    const interval = window.setInterval(loadMessages, 4000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [userId]);

  useEffect(() => {
    const container = messagesRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if ((!text && !attachment) || sending) return;

    setSending(true);
    setError("");
    try {
      const message = await sendSupportMessage(userId, text, attachment);
      setMessages((current) => [...current, message]);
      lastMessageIdRef.current = Math.max(lastMessageIdRef.current, message.id);
      setDraft("");
      setAttachment(null);
    } catch {
      setError("Сообщение не отправлено. Попробуйте ещё раз");
    } finally {
      setSending(false);
    }
  };

  return (
    <Page>
      <H1>Поддержка</H1>
      <Lead>Вместо переписки через команды — компактный чат с историей и явным завершением.</Lead>
      <SupportChat>
        <SupportMessages ref={messagesRef}>
          {loading && <SmallText>Загружаем историю переписки...</SmallText>}
          {!loading && !messages.length && (
            <SmallText style={{ margin: "auto", maxWidth: 260, textAlign: "center" }}>
              Опишите вопрос. Оператор увидит сообщение в группе поддержки и ответит здесь.
            </SmallText>
          )}
          {messages.map((message) => {
            const operator = message.sender === "operator";
            return (
              <SupportBubble key={message.id} $operator={operator}>
                <Icon src={operator ? supportIcon : uidIcon} $size={42} />
                <div>
                  <TitleText style={{ fontSize: 12 }}>{operator ? "Оператор" : "Вы"}</TitleText>
                  <SmallText style={{ marginTop: 4, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{message.text}</SmallText>
                  {message.media_url && (
                    <SupportMedia>
                      {message.media_type?.startsWith("image/") ? (
                        <a href={message.media_url} target="_blank" rel="noreferrer"><img src={message.media_url} alt={message.media_name || "Вложение"} /></a>
                      ) : message.media_type?.startsWith("video/") ? (
                        <video src={message.media_url} controls preload="metadata" />
                      ) : message.media_type?.startsWith("audio/") ? (
                        <audio src={message.media_url} controls preload="metadata" />
                      ) : (
                        <a href={message.media_url} target="_blank" rel="noreferrer"><FileText size={15} />{message.media_name || "Открыть вложение"}</a>
                      )}
                    </SupportMedia>
                  )}
                </div>
              </SupportBubble>
            );
          })}
        </SupportMessages>
        <SupportComposer onSubmit={submit}>
          {attachment && (
            <SupportAttachment>
              <Paperclip size={14} />
              <span>{attachment.name}</span>
              <button type="button" onClick={() => setAttachment(null)} aria-label="Убрать вложение"><X size={15} /></button>
            </SupportAttachment>
          )}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*,video/*,audio/*,.pdf,.zip,.doc,.docx"
            onChange={(event) => {
              const file = event.target.files?.[0] || null;
              if (file && file.size > 20 * 1024 * 1024) {
                setError("Размер файла не должен превышать 20 МБ");
                event.currentTarget.value = "";
                return;
              }
              setAttachment(file);
              setError("");
              event.currentTarget.value = "";
            }}
          />
          <SupportAttachButton type="button" onClick={() => fileInputRef.current?.click()} aria-label="Прикрепить медиа"><Paperclip size={20} /></SupportAttachButton>
          <SupportTextarea
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            maxLength={4000}
            placeholder="Напишите сообщение"
            aria-label="Сообщение поддержке"
          />
          <SupportSendButton type="submit" disabled={(!draft.trim() && !attachment) || sending} aria-label="Отправить сообщение"><SendHorizontal size={20} /></SupportSendButton>
        </SupportComposer>
        {error && <ErrorLine>{error}</ErrorLine>}
      </SupportChat>
    </Page>
  );
}

function Catalog({
  products,
  selectedIds,
  setSelectedIds,
  setScreen,
  initialFilter,
  setCatalogFilter,
}: {
  products: Product[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  setScreen: (screen: Screen) => void;
  initialFilter?: "subscription" | "study";
  setCatalogFilter: (filter: "subscription" | "study") => void;
}) {
  const [filter, setFilter] = useState<"subscription" | "study">(initialFilter || "subscription");
  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);
  const visibleProducts = products.filter((product) => product.kind === filter);

  const toggleProduct = (id: string) => {
    const next = selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id];
    setSelectedIds(next);
  };

  return (
    <Page style={{ minHeight: "calc(100svh + 220px)" }}>
      <H1>Каталог</H1>
      <Lead>Здесь Вы сможете выбрать несколько услуг. Просто кликните на те услуги, которые Вам понравились:</Lead>
      <Chips>
        <Chip $active={filter === "subscription"} onClick={() => { setFilter("subscription"); setCatalogFilter("subscription"); }}>Подписки</Chip>
        <Chip $active={filter === "study"} onClick={() => { setFilter("study"); setCatalogFilter("study"); }}>Обучение</Chip>
      </Chips>
      <RowCard style={{ marginTop: 14, minHeight: 80, padding: "12px 14px", gridTemplateColumns: "58px minmax(0,1fr) 82px", gap: 12 }} onClick={() => setScreen("free")}> 
        <Icon src={promoExclusiveIcon} $size={58} />
        <div>
          <TitleText style={{ fontSize: 14 }}>Эксклюзивный канал</TitleText>
          <SmallText>Бесплатно для рефералов бирж</SmallText>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 4 }}>
            <span style={{ color: COLORS.green, fontWeight: 900 }}>0 USDT</span>
            <span style={{ color: "#ffd92e", fontSize: 9, fontWeight: 700 }}>для рефералов</span>
          </div>
        </div>
        <GlowButton style={{ width: 82 }}>Получить</GlowButton>
      </RowCard>
      <SectionTitle style={{ marginTop: 20, marginBottom: 0 }}>
        {filter === "subscription" ? "Выберите подписку:" : "Выберите обучение:"}
      </SectionTitle>
      <div style={{ display: "grid", gap: 14, marginTop: 20 }}>
        {visibleProducts.map((product) => (
          <RowCard key={product.id} $selected={selectedIds.includes(product.id)} onClick={() => toggleProduct(product.id)} style={{ gridTemplateColumns: "46px 1fr" }}>
            <Icon src={product.icon} />
            <div>
              <TitleText style={{ fontSize: 14 }}>{product.title}</TitleText>
              <SmallText>{product.description}</SmallText>
            </div>
          </RowCard>
        ))}
      </div>
      <PrimaryButton style={{ marginTop: 20 }} disabled={!selectedIds.length} onClick={() => setScreen("config")}>Дальше</PrimaryButton>
    </Page>
  );
}

function Configurator({
  selectedProducts,
  discounts,
  duration,
  setDuration,
  setScreen,
}: {
  selectedProducts: Product[];
  discounts: MiniAppDiscounts;
  duration: number | null;
  setDuration: (days: number | null) => void;
  setScreen: (screen: Screen) => void;
}) {
  const durationOptions = getDurationOptions(discounts);
  const selectedDuration = durationOptions.find((item) => item.days === duration) || { days: 0, discount: 0 };
  const isPackage = selectedProducts.length > 1;
  const subscriptions = selectedProducts.filter((product) => product.kind === "subscription");
  const studies = selectedProducts.filter((product) => product.kind === "study");
  const packageDiscount = subscriptions.length > 1
    ? volumeDiscount(subscriptions.length, discounts)
    : 0;

  const renderProducts = (items: Product[]) => items.map((product, index) => (
    <div key={product.id} style={{ display: "grid", gridTemplateColumns: "46px 1fr 52px", gap: 10, alignItems: "center", marginTop: index ? 18 : 10 }}>
      <Icon src={product.icon} />
      <div>
        <TitleText>{product.title}</TitleText>
        <SmallText>{product.price} USDT{product.kind === "subscription" ? ` / ${product.days} дней` : ""}</SmallText>
      </div>
      {product.kind === "subscription" ? (
        <div style={{ color: "#23d18b", fontFamily: "Unbounded", fontWeight: 900, lineHeight: 1, textAlign: "right" }}>
          <span style={{ display: "block" }}>-{selectedDuration.discount}</span>
          <span style={{ display: "block", marginTop: 4 }}>%</span>
        </div>
      ) : <span />}
    </div>
  ));

  return (
    <Page>
      <H1>{isPackage ? "Пакет подписок" : selectedProducts[0]?.title || "Подписка"}</H1>
      <Lead>Выбранные услуги{isPackage ? `: ${selectedProducts.length}` : ":"}</Lead>
      <Card style={{ marginTop: 24, padding: 15 }}>
        {studies.length > 0 && <SectionTitle style={{ margin: 0, fontSize: 16 }}>Обучение:</SectionTitle>}
        {renderProducts(studies)}
        {subscriptions.length > 0 && <SectionTitle style={{ margin: studies.length ? "22px 0 0" : 0, fontSize: 16 }}>Подписки:</SectionTitle>}
        {renderProducts(subscriptions)}
        {subscriptions.length > 1 && packageDiscount > 0 && <AnimatedReveal><RowCard style={{ marginTop: 18, minHeight: 78 }}>
          <Icon src={autoDiscountIcon} />
          <div>
            <TitleText style={{ fontSize: 14 }}>Автоскидка за объем</TitleText>
            <SmallText>{`${subscriptions.length} подписки вместе: -${packageDiscount}% к сумме после скидки за срок.`}</SmallText>
          </div>
          <span />
        </RowCard></AnimatedReveal>}
      </Card>
      {subscriptions.length > 0 && <>
        <SectionTitle>Срок подписки</SectionTitle>
        <DurationGrid>
          {durationOptions.map((item) => (
            <DurationButton key={item.days} $active={duration === item.days} $wide={item.days === 360} onClick={() => setDuration(item.days)}>
              <span>{item.days} дн</span>
              <span>{item.discount}%</span>
            </DurationButton>
          ))}
        </DurationGrid>
      </>}
      <div style={{ position: "relative", height: 178, marginTop: 8 }}>
        <OrderRobot
          src={saymonPoint}
          alt=""
          style={{
            width: 184,
            height: 175,
            objectFit: "contain",
            display: "block",
            position: "absolute",
            left: "calc(50% + 68px)",
            bottom: 18,
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
        <PrimaryButton
          style={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 2 }}
          disabled={subscriptions.length > 0 && duration === null}
          onClick={() => setScreen("checkout")}
        >
          Перейти к заказу
        </PrimaryButton>
        <OrderRobot
          src={saymonPoint}
          alt=""
          style={{
            width: 184,
            height: 175,
            objectFit: "contain",
            display: "block",
            position: "absolute",
            left: "calc(50% + 68px)",
            bottom: 18,
            clipPath: "polygon(0 55%, 34% 55%, 34% 100%, 0 100%)",
            zIndex: 3,
            pointerEvents: "none",
          }}
        />
      </div>
      <div style={{ height: 12 }} />
    </Page>
  );
}

function Checkout({
  selectedProducts,
  discounts,
  duration,
  balance,
  setScreen,
}: {
  selectedProducts: Product[];
  discounts: MiniAppDiscounts;
  duration: number | null;
  balance: number;
  setScreen: (screen: Screen) => void;
}) {
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState("");
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState<"cryptomus" | "tonconnect" | "" >("");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [balanceError, setBalanceError] = useState(0);
  useEffect(() => {
    sessionStorage.setItem("saymon.manual.order", JSON.stringify({ promoDiscount }));
  }, [promoDiscount]);
  const durationDiscount = getDurationOptions(discounts).find((item) => item.days === duration)?.discount || 0;
  const subscriptions = selectedProducts.filter((product) => product.kind === "subscription");
  const studies = selectedProducts.filter((product) => product.kind === "study");
  const subscriptionBase = subscriptions.reduce((sum, product) => sum + product.price * (duration ? duration / 30 : 1), 0);
  const studyBase = studies.reduce((sum, product) => sum + product.price, 0);
  const base = studyBase + subscriptionBase;
  const termDiscount = subscriptionBase * durationDiscount / 100;
  const afterTerm = base - termDiscount;
  const packageDiscount = subscriptions.length > 1
    ? volumeDiscount(subscriptions.length, discounts)
    : 0;
  const volume = subscriptionBase * packageDiscount / 100;
  const beforePromo = Math.max(0, afterTerm - volume);
  const promoSaving = beforePromo * promoDiscount / 100;
  const total = Math.max(0, beforePromo - promoSaving);
  const balanceIsInsufficient = balance < total;

  const applyPromo = async () => {
    const value = promoCode.trim();
    if (!value) {
      setPromoDiscount(0);
      setPromoMessage("Введите промокод");
      return;
    }
    setCheckingPromo(true);
    setPromoMessage("");
    try {
      const promo = await checkPromoCode(value, getUserId());
      const percent = Math.max(0, Number(promo.percent) || 0);
      setPromoDiscount(percent);
      setPromoMessage(`Скидка ${percent}% добавлена к заказу.`);
    } catch (error) {
      setPromoDiscount(0);
      setPromoMessage("Промокод уже использован или привязан к другой подписке.");
    } finally {
      setCheckingPromo(false);
    }
  };

  const openPayment = async (method: "cryptomus" | "tonconnect") => {
    setPaymentMessage("");
    setPaymentLoading(method);
    try {
      const payment = await createMiniAppPayment(getUserId(), total, method, "TON");
      // Keep the provider inside Telegram's current WebView instead of opening an external browser.
      window.location.assign(payment.url);
    } catch (error) {
      setPaymentMessage(error instanceof Error ? error.message : "Не удалось открыть оплату");
    } finally {
      setPaymentLoading("");
    }
  };

  return (
    <Page>
      <H1>Ваш заказ</H1>
      <Lead>Проверьте содержимое заказа и пролистните вниз, чтобы выбрать способ оплаты:</Lead>
      <div style={{ display: "grid", gap: 12, marginTop: 28 }}>
        {studies.length > 0 && <SectionTitle style={{ margin: 0, fontSize: 16 }}>Обучение:</SectionTitle>}
        {studies.map((product) => (
          <RowCard key={product.id} style={{ minHeight: 88 }}>
            <Icon src={product.icon} />
            <div>
              <TitleText style={{ fontSize: 14 }}>{product.title}</TitleText>
              <SmallText>Базовая сумма: {product.price} USDT.</SmallText>
            </div>
            <span />
          </RowCard>
        ))}
        {subscriptions.length > 0 && <SectionTitle style={{ margin: studies.length ? "18px 0 0" : 0, fontSize: 16 }}>Подписки:</SectionTitle>}
        {subscriptions.map((product) => (
          <RowCard key={product.id} style={{ minHeight: 88 }}>
            <Icon src={product.icon} />
            <div>
              <TitleText style={{ fontSize: 14 }}>{product.title}, {duration || "срок не выбран"}{duration ? " дней" : ""}</TitleText>
              <SmallText>Базовая сумма: {product.price} USDT. Скидка срока: {durationDiscount}%.</SmallText>
            </div>
            <span />
          </RowCard>
        ))}
      </div>
      <Summary style={{ marginTop: 16 }}>
        <TitleText>Расчет</TitleText>
        <SummaryRow><span>Подписка</span><strong>{money(base)}</strong></SummaryRow>
        <SummaryRow><span>Скидка срока</span><strong>-{money(termDiscount)}</strong></SummaryRow>
        <SummaryRow><span>Скидка за объем</span><strong>{volume ? `-${money(volume)}` : "нет"}</strong></SummaryRow>
        <SummaryRow><span>Промокод</span><strong>{promoDiscount ? `-${promoDiscount}%` : "не применен"}</strong></SummaryRow>
        <SummaryRow><span>Реф. баланс</span><strong style={{ color: balanceIsInsufficient ? COLORS.danger : COLORS.green }}>{money(balance)}</strong></SummaryRow>
        <div style={{ height: 1, background: COLORS.line, margin: "8px 0" }} />
        <SummaryRow $total><span>Итого</span><strong>{money(total)}</strong></SummaryRow>
      </Summary>
      <RowCard style={{ marginTop: 14, minHeight: 84, display: "block", padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Icon src={promoCodeIcon} $size={42} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <TitleText style={{ fontSize: 14 }}>Промокод</TitleText>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 92px", gap: 8, marginTop: 8 }}>
              <Input value={promoCode} onChange={(event) => setPromoCode(event.target.value)} placeholder="Введите код" style={{ height: 42, padding: "0 12px" }} />
              <GhostButton type="button" style={{ height: 42, minHeight: 42 }} onClick={applyPromo} disabled={checkingPromo}>{checkingPromo ? "..." : "Применить"}</GhostButton>
            </div>
          </div>
        </div>
        {promoMessage && (
          <AnimatedReveal><div style={{ display: "grid", gridTemplateColumns: "58px minmax(0, 1fr)", gap: 12, alignItems: "center", marginTop: 16, padding: "14px 12px", border: `1px solid ${promoDiscount ? "rgba(0, 237, 184, 0.24)" : "rgba(143, 110, 255, 0.24)"}`, borderRadius: 16, background: promoDiscount ? "rgba(14, 39, 62, 0.72)" : "rgba(14, 14, 24, 0.8)" }}>
            <div style={{ position: "relative", width: 52, height: 52 }}>
              <Icon src={promoDiscount ? promoCodeIcon : promoCodeIcon} $size={52} />
              {!promoDiscount && (
                <span style={{ position: "absolute", right: -2, top: -3, width: 22, height: 22, display: "grid", placeItems: "center", borderRadius: "50%", background: "#e51d24", color: "#fff", boxShadow: "0 0 0 3px #0e0e18" }}>
                  <X size={14} strokeWidth={3} />
                </span>
              )}
            </div>
            <div>
              <TitleText style={{ fontSize: 18 }}>{promoDiscount ? "Код применен" : "Код не подходит"}</TitleText>
              <Lead style={{ marginTop: 6, fontSize: 14, lineHeight: 1.25 }}>{promoMessage}</Lead>
            </div>
          </div></AnimatedReveal>
        )}
      </RowCard>
      <SectionTitle style={{ fontSize: 24 }}>Оплата</SectionTitle>
      <div style={{ display: "grid", gap: 14 }}>
        <PaymentCard icon={cryptomusIcon} title="Cryptomus" text={paymentLoading === "cryptomus" ? "Создаем ссылку на оплату..." : "Оплата картой или криптовалютой."} onClick={() => openPayment("cryptomus")} />
        <PaymentCard icon={tonIcon} title="TON Connect" text={paymentLoading === "tonconnect" ? "Создаем ссылку на оплату..." : "Оплата через TON Connect."} onClick={() => openPayment("tonconnect")} />
        <PaymentCard icon={walletIcon} title="Реферальный баланс" text={balanceIsInsufficient ? "Недостаточно средств для этого заказа." : "Оплатить заказ с реферального баланса."} textTone={balanceError ? COLORS.danger : undefined} shakeKey={balanceError} badge={`${formatNumberWithSpace(balance)} $`} badgeTone={balanceIsInsufficient ? COLORS.danger : COLORS.green} onClick={() => { if (balanceIsInsufficient || (subscriptions.length > 0 && duration === null)) { setBalanceError((value) => value + 1); return; } openBotCheckout(selectedProducts, duration || 0, promoCode); }} />
        <PaymentCard icon={supportIcon} title="Другой способ оплаты" text="Ручная оплата по реквизитам с проверкой оператором." onClick={() => setScreen("manual")} />
      </div>
      {paymentMessage && <SmallText style={{ color: COLORS.danger, marginTop: 12 }}>{paymentMessage}</SmallText>}
    </Page>
  );
}

function PaymentCard({ icon, title, text, badge, badgeTone, textTone, shakeKey, onClick }: { icon: string; title: string; text: string; badge?: string; badgeTone?: string; textTone?: string; shakeKey?: number; onClick: () => void }) {
  return (
    <RowCard onClick={onClick} style={{ cursor: "pointer" }}>
      <Icon src={icon} />
      <div>
        <TitleText style={{ fontSize: 14 }}>{title}</TitleText>
        {textTone ? <BalanceErrorText key={shakeKey} $shakeKey={shakeKey}>{text}</BalanceErrorText> : <SmallText>{text}</SmallText>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 24 }}>
        {badge && <div style={{ color: badgeTone || "#14ffa3", fontSize: 10, fontWeight: 900, marginBottom: 8 }}>{badge}</div>}
        <span style={{ fontSize: 24 }}>›</span>
      </div>
    </RowCard>
  );
}

function StatusScreen({ kind, setScreen, selectedProducts = [], discounts = defaultDiscounts, duration = null }: { kind: "waiting" | "paid" | "manual" | "manual-payeer" | "manual-submitted"; setScreen: (screen: Screen) => void; selectedProducts?: Product[]; discounts?: MiniAppDiscounts; duration?: number | null }) {
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualPayment, setManualPayment] = useState<{ amountUsdt: number; amountRub: number; wallet: string } | null>(null);
  const [walletCopyNotice, setWalletCopyNotice] = useState("");
  const [activeSubscriptions, setActiveSubscriptions] = useState<IUserActiveSubscription[]>([]);
  const subscriptions = selectedProducts.filter((product) => product.kind === "subscription");
  const subscriptionBase = subscriptions.reduce((sum, product) => sum + product.price * (duration ? duration / 30 : 1), 0);
  const studyBase = selectedProducts.filter((product) => product.kind === "study").reduce((sum, product) => sum + product.price, 0);
  const baseAmount = studyBase + subscriptionBase;
  const durationDiscount = getDurationOptions(discounts).find((item) => item.days === duration)?.discount || 0;
  const durationSaving = subscriptionBase * durationDiscount / 100;
  const volumeRate = subscriptions.length > 1 ? volumeDiscount(subscriptions.length, discounts) : 0;
  const volumeSaving = subscriptionBase * volumeRate / 100;
  const savedPromo = (() => { try { return Number(JSON.parse(sessionStorage.getItem("saymon.manual.order") || "{}").promoDiscount) || 0; } catch { return 0; } })();
  const amountUsdt = Math.max(0, baseAmount - durationSaving - volumeSaving) * (1 - savedPromo / 100);
  const copyWallet = async (wallet: string) => {
    try {
      await navigator.clipboard.writeText(wallet);
      setWalletCopyNotice("Скопировано успешно");
    } catch {
      setWalletCopyNotice("Не удалось скопировать кошелек");
    }
    window.setTimeout(() => setWalletCopyNotice(""), 2200);
  };
  useEffect(() => {
    if (kind !== "manual-payeer" || amountUsdt <= 0) return;
    void getMiniAppManualQuote(amountUsdt).then((quote) => setManualPayment({ amountUsdt: quote.amount_usdt, amountRub: quote.amount_rub, wallet: quote.wallet })).catch(() => undefined);
  }, [amountUsdt, kind]);
  useEffect(() => {
    if (kind !== "paid") return;
    void getUserActiveSubscriptions(getUserId()).then(setActiveSubscriptions).catch(() => setActiveSubscriptions([]));
  }, [kind]);
  if (kind === "manual") {
    return (
      <Page>
        <H1>Другие способы оплаты</H1>
        <Lead>Ручные способы подтверждаются администратором. Выберите, куда будете переводить деньги.</Lead>
        <Card style={{ marginTop: 24, padding: 14 }}>
          <PaymentCard icon={walletIcon} title="Payeer" text="Оплата в рублях по реквизитам. После перевода нажмите «Оплачено»." onClick={() => setScreen("manual-payeer")} />
          <Card style={{ marginTop: 14, padding: 16 }}>
            <TitleText style={{ fontSize: 14 }}>Важно</TitleText>
            <SmallText style={{ display: "block", marginTop: 8 }}>Ручные оплаты проверяет администратор. Если администратор онлайн, подтверждение обычно быстрее; если нет — заявка ожидает проверки.</SmallText>
          </Card>
        </Card>
        <GhostButton style={{ marginTop: 14 }} onClick={() => setScreen("checkout")}>Назад</GhostButton>
      </Page>
    );
  }

  if (kind === "manual-payeer") {
    return (
      <Page>
        <H1>Payeer</H1>
        <Lead>Оплатите указанную сумму по реквизитам, затем подтвердите перевод.</Lead>
        <Card style={{ marginTop: 24, padding: 20 }}>
          <SmallText>Вы выбрали</SmallText>
          <TitleText style={{ marginTop: 8, fontSize: 18 }}>Ваш заказ</TitleText>
          <SmallText style={{ display: "block", marginTop: 22 }}>Итоговая сумма</SmallText>
          <div style={{ color: COLORS.green, fontFamily: "Unbounded", fontWeight: 900, fontSize: 18, marginTop: 6 }}>{manualPayment ? `${formatNumberWithSpace(manualPayment.amountUsdt)} USDT = ${formatNumberWithSpace(manualPayment.amountRub)} RUB` : `${formatNumberWithSpace(amountUsdt)} USDT`}</div>
          <SmallText style={{ display: "block", marginTop: 22 }}>Кошелёк Payeer</SmallText>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}><div style={{ color: COLORS.blue, fontFamily: "Unbounded", fontWeight: 900, fontSize: 18 }}>{manualPayment?.wallet || "P23890793"}</div><GhostButton type="button" aria-label="Скопировать кошелек" style={{ height: 34, minHeight: 34, width: 42, padding: 0 }} onClick={() => void copyWallet(manualPayment?.wallet || "P23890793")}><Copy size={16} /></GhostButton></div>
          {walletCopyNotice && <CopyNotice $success={walletCopyNotice.includes("успешно")} style={{ marginTop: 12 }}>{walletCopyNotice}</CopyNotice>}
          <SmallText style={{ display: "block", marginTop: 18 }}>После перевода нажмите «Оплачено», чтобы отправить заявку администратору.</SmallText>
        </Card>
        {manualError && <ErrorLine style={{ marginTop: 12 }}>{manualError}</ErrorLine>}
        <PrimaryButton style={{ marginTop: 20 }} disabled={manualLoading} onClick={async () => { setManualLoading(true); setManualError(""); try { const result = await createMiniAppManualPayment({ userId: getUserId(), amountUsdt, tariffIds: selectedProducts.map((product) => product.id), items: selectedProducts.map((product) => `${product.title} (${product.price} USDT)`), duration, promoDiscount: savedPromo }); setManualPayment({ amountUsdt: result.amount_usdt, amountRub: result.amount_rub, wallet: result.wallet }); setScreen("manual-submitted"); } catch (error) { setManualError(error instanceof Error ? error.message : "Не удалось отправить заявку"); } finally { setManualLoading(false); } }}>{manualLoading ? "Отправляем..." : "Оплачено"}</PrimaryButton>
        <GhostButton style={{ marginTop: 12 }} onClick={() => setScreen("manual")}>Назад</GhostButton>
      </Page>
    );
  }

  if (kind === "manual-submitted") {
    return (
      <Page>
        <H1>Оплата на проверке</H1>
        <Lead>Заявка отправлена администратору. Доступ откроется после подтверждения оплаты.</Lead>
        <Card style={{ marginTop: 24, padding: 20 }}>
          <TitleText style={{ fontSize: 18 }}>Готово</TitleText>
          <SmallText style={{ display: "block", marginTop: 12 }}>Ваша оплата отправлена на обработку. В течение 24 часов администратор проверит перевод.</SmallText>
          <SmallText style={{ display: "block", marginTop: 20 }}>Способ оплаты</SmallText>
          <TitleText style={{ color: COLORS.blue, marginTop: 6 }}>Payeer</TitleText>
          <TitleText style={{ color: COLORS.green, fontSize: 14, marginTop: 12 }}>Статус: ожидает проверки</TitleText>
        </Card>
        <PrimaryButton style={{ marginTop: 20 }} onClick={() => setScreen("profile")}>Вернуться в профиль</PrimaryButton>
        <GhostButton style={{ marginTop: 12 }} onClick={() => setScreen("home")}>Вернуться на главную</GhostButton>
      </Page>
    );
  }

  if (kind === "paid") {
    const activeItems = activeSubscriptions.length
      ? activeSubscriptions
      : (selectedProducts.filter((product) => product.kind === "subscription").map((product) => ({ name: product.title, days_remaining: duration || 0, channel_id: 0, kind: "subscription" as const })));
    return (
      <Page>
        <H1>Готово</H1>
        <Lead>Здесь Вы можете просмотреть и перейти на каналы из подписок и также прочитать инструкции к ним</Lead>
        <Card style={{ position: "relative", overflow: "hidden", marginTop: 22, minHeight: 112, padding: 18, display: "flex", alignItems: "center" }}>
          <div style={{ minWidth: 0, paddingRight: 128 }}>
            <TitleText style={{ fontSize: 14 }}>Подписка активна</TitleText>
            <SmallText style={{ display: "block", marginTop: 12, whiteSpace: "pre-line" }}>{activeItems.map((item) => item.kind === "study" ? item.name : `${item.name} — ${item.days_remaining} дн.`).join("\n")}</SmallText>
          </div>
          <img src={saymonDashboard} alt="" style={{ position: "absolute", right: -8, bottom: -22, width: 190, height: 190, objectFit: "contain" }} />
          <Icon src={exclusiveIcon} $size={38} style={{ position: "absolute", right: 12, bottom: 14 }} />
        </Card>
        <Card style={{ marginTop: 18, padding: 18 }}>
          <TitleText style={{ fontSize: 16 }}>Доступы</TitleText>
          {activeItems.map((item) => (
            <PaymentCard
              key={`${item.kind}-${item.name}-${item.channel_id}`}
              icon={item.kind === "study" ? learningIcon : exclusiveIcon}
              title={item.kind === "study" ? item.name : `Канал «${item.name}»`}
              text={item.kind === "study" ? "Открыть материалы и инструкцию." : "Открыть закреплённый канал и материалы."}
              onClick={() => setScreen(item.kind === "study" ? "subscriptions" : "profile")}
            />
          ))}
          <PaymentCard icon={learningIcon} title="Инструкция" text="Что делать после вступления в канал." onClick={() => setScreen("subscriptions")} />
        </Card>
        <PrimaryButton style={{ marginTop: 26 }} onClick={() => setScreen("profile")}>Назад в профиль</PrimaryButton>
      </Page>
    );
  }
  const copy = ["Ожидание оплаты", "Мы проверяем перевод. После подтверждения доступ откроется автоматически.", "Проверить статус"];
  return (
    <Page $short>
      <H1>{copy[0]}</H1>
      <Lead>{copy[1]}</Lead>
      <Card style={{ marginTop: 28, padding: 20, textAlign: "center" }}>
        <Icon src={supportIcon} $size={96} />
        <TitleText style={{ marginTop: 12 }}>{copy[0]}</TitleText>
      </Card>
      <PrimaryButton style={{ marginTop: 20 }} onClick={() => setScreen("checkout")}>{copy[2]}</PrimaryButton>
    </Page>
  );
}

function FreeChannel({ success, setScreen }: { success: boolean; setScreen: (screen: Screen) => void }) {
  const [exchange, setExchange] = useState("BingX");
  const [uid, setUid] = useState("");
  const [error, setError] = useState("");

  const check = async () => {
    const value = uid.trim();
    if (!/^[A-Za-z0-9_-]{5,32}$/.test(value)) {
      setError("UID не подходит: укажите 5-32 символа без пробелов.");
      return;
    }
    setError("");
    try {
      const res = await fetch(`${apiUrl}/user/exchange-access/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exchange, uid: value, tg_id: getUserId() }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.eligible === false) {
          setError("UID не подходит: аккаунт не найден в рефералке VILARSO.");
          return;
        }
      }
    } catch {
      // If the backend route is not deployed yet, keep the UI flow usable.
    }
    setScreen("free-success");
  };

  if (success) {
    return (
      <Page $short>
        <H1>Эксклюзивный канал</H1>
        <Lead>Рефералка подтверждена. Бесплатный эксклюзивный канал доступен в вашем профиле.</Lead>
        <Card style={{ marginTop: 20, padding: 18, display: "grid", gridTemplateColumns: "70px 1fr 124px", gap: 8, alignItems: "center" }}>
          <Icon src={exclusiveIcon} $size={62} />
          <div>
            <TitleText style={{ fontSize: 13 }}>Эксклюзивный канал активен</TitleText>
            <SmallText>Бесплатно</SmallText>
          </div>
          <img src={saymonDashboard} alt="" style={{ width: 120, height: 88, objectFit: "cover", objectPosition: "top" }} />
        </Card>
        <SectionTitle>Доступы</SectionTitle>
        <PaymentCard icon={exclusiveIcon} title="Эксклюзивный канал" text="Приватный канал для рефералов бирж." onClick={() => window.open("https://t.me/", "_blank")} />
        <PaymentCard icon={learningIcon} title="Как пользоваться доступом" text="Правила канала и действия после вступления." onClick={() => setScreen("subscriptions")} />
        <PrimaryButton style={{ marginTop: 26 }} onClick={() => setScreen("profile")}>Назад в профиль</PrimaryButton>
      </Page>
    );
  }

  return (
    <Page>
      <H1>Биржевой доступ</H1>
      <Lead>Выберите биржу и UID. Если аккаунт найден в рефералке VILARSO, откроем бесплатный эксклюзивный канал.</Lead>
      <SectionTitle style={{ marginBottom: 8 }}>Биржа</SectionTitle>
      <Chips style={{ marginTop: 0 }}>
        {Object.keys(exchangeLinks).map((item) => (
          <Chip key={item} $active={exchange === item} onClick={() => setExchange(item)}>{item}</Chip>
        ))}
      </Chips>
      <Card style={{ marginTop: 8, minHeight: 54, padding: 10, display: "grid", gridTemplateColumns: "1fr 112px", gap: 10, alignItems: "center" }}>
        <div>
          <TitleText style={{ fontSize: 11 }}>Условие</TitleText>
          <SmallText>Зарегистрируйтесь на бирже по ссылке VILARSO, затем укажите UID для проверки.</SmallText>
        </div>
        <GlowButton onClick={() => window.open(exchangeLinks[exchange], "_blank")}>Открыть ссылку</GlowButton>
      </Card>
      <div style={{ position: "relative", marginTop: 10 }}>
        <Input $error={Boolean(error)} placeholder="UID / Account ID" value={uid} onChange={(event) => setUid(event.target.value)} />
        <Icon src={uidIcon} $size={34} style={{ position: "absolute", right: 13, top: 9 }} />
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      <Card style={{ marginTop: 10, minHeight: 78, padding: "11px 16px 11px 66px" }}>
        <Icon src={exclusiveIcon} $size={52} style={{ position: "absolute", left: 8, top: 10 }} />
        <TitleText style={{ fontSize: 14 }}>Бесплатный эксклюзивный канал</TitleText>
        <SmallText>Доступ после проверки рефералки на BingX, Bitget или OKX.</SmallText>
        <div style={{ display: "flex", gap: 14, marginTop: 6, alignItems: "center" }}>
          <span style={{ color: COLORS.green, fontWeight: 900, fontSize: 16 }}>0 USDT</span>
          <span style={{ color: "#ffd92e", fontWeight: 800, fontSize: 10 }}>для рефералов</span>
        </div>
      </Card>
      <Card style={{ marginTop: 10, minHeight: 82, padding: 13 }}>
        <Icon src={refCheckIcon} $size={30} style={{ position: "absolute", right: 18, top: 14 }} />
        <TitleText style={{ fontSize: 14 }}>Что проверяем</TitleText>
        <SmallText style={{ lineHeight: "16px", marginTop: 9 }}>1. Биржа есть в списке VILARSO<br />2. UID найден как реферал<br />3. Канал еще не был выдан</SmallText>
      </Card>
      <PrimaryButton style={{ marginTop: 8 }} onClick={check}>Проверить доступ</PrimaryButton>
      <GhostButton style={{ marginTop: 12 }} onClick={() => setScreen("manual")}>Не нашел UID? Написать в поддержку</GhostButton>
    </Page>
  );
}
function ActiveSubscriptions() {
  const [items, setItems] = useState<IUserActiveSubscription[]>([]);

  useEffect(() => {
      withTimeout(getUserActiveSubscriptions(getUserId())).then(setItems).catch(() => setItems([]));
  }, []);

  return (
    <Page $short>
      <H1>Активные подписки</H1>
      <Lead>Все открытые доступы, сроки и инструкции после оплаты.</Lead>
      <Card style={{ marginTop: 24, padding: 16 }}>
        <TitleText>Доступно сейчас</TitleText>
        <SmallText>{items.length ? `Открыто ${items.length} доступа` : "Нет активных подписок"}</SmallText>
      </Card>
      {items.length > 0 && (
        <div style={{ display: "grid", gap: 14, marginTop: 18 }}>
          {items.map((item) => {
            const lowerName = item.name.toLowerCase();
            return (
              <PaymentCard
                key={`${item.name}-${item.channel_id}`}
                icon={item.kind === "study" ? learningIcon : lowerName.includes("аналит") ? analyticsIcon : lowerName.includes("экск") ? exclusiveIcon : indicatorIcon}
                title={item.name}
                text={item.kind === "study" ? "Материалы и инструкция доступны после оплаты." : `${item.days_remaining} дней осталось. Инструкция доступна после входа.`}
                badge="Открыть"
                onClick={() => window.open("https://t.me/", "_blank")}
              />
            );
          })}
        </div>
      )}
    </Page>
  );
}
function Nickname({ setScreen }: { setScreen: (screen: Screen) => void }) {
  const [nick, setNick] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getUserProfile(getUserId()).then((profile) => setNick(profile?.trading_view_name || "")).catch(() => setNick(""));
  }, []);

  const saveNickname = async () => {
    const nextNick = nick.trim();
    setError("");
    if (nextNick.length < 3) {
      setError("Введите никнейм TradingView, минимум 3 символа");
      return;
    }

    try {
      setSaving(true);
      await updateTradingViewName(getUserId(), nextNick);
      setScreen("profile");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось сохранить никнейм");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page $short>
      <H1>Смена никнейма</H1>
      <Lead>Укажите ник TradingView, чтобы команда могла корректно выдать доступы и сверить профиль.</Lead>
      <Input value={nick} onChange={(event) => setNick(event.target.value)} placeholder="TradingView nickname" style={{ marginTop: 24, paddingRight: 15 }} />
      {error && <ErrorLine>{error}</ErrorLine>}
      <PrimaryButton style={{ marginTop: 14 }} onClick={saveNickname} disabled={saving}>{saving ? "Сохраняем..." : "Сохранить"}</PrimaryButton>
      <GhostButton style={{ marginTop: 12 }} onClick={() => setScreen("profile")}>Отмена</GhostButton>
    </Page>
  );
}
function LeaderAvatar({ user, size = 46 }: { user: IUser; size?: number }) {
  return user.image ? <Avatar src={user.image} $size={size} /> : <Avatar as="div" $size={size} />;
}

function LeaderBadges({ user }: { user: IUser }) {
  const position = leaderPosition(user);
  const invited = numberOrZero(user.invited_count);
  const change = numberOrZero(user.change);
  return (
    <BadgeRow>
      <Pill>{position > 0 ? `#${position}` : "#-"}</Pill>
      <Pill $tone="dark">{invited} реф.</Pill>
      <Pill $tone={change >= 0 ? "green" : "dark"}>{change > 0 ? `+${change}` : change}</Pill>
    </BadgeRow>
  );
}

function LeadersV2() {
  const [users, setUsers] = useState<IUser[]>([]);
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    const userId = getUserId();

    setLoading(true);
    setError("");
    Promise.all([
      getTopUsers(1),
      userId > 0 ? getUserLiderboardByTg(userId).catch(() => null) : Promise.resolve(null),
    ])
      .then(([leaders, currentUser]) => {
        if (!isMounted) return;
        setUsers(Array.isArray(leaders.users) ? leaders.users : []);
        setUser(currentUser);
      })
      .catch(() => {
        if (!isMounted) return;
        setUsers([]);
        setUser(null);
        setError("Лидерборд пока недоступен");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const list = users.slice(0, 5);
  const currentUser = user || list[0] || null;

  return (
    <Page>
      <H1>Лидеры</H1>
      <Lead>Таблица партнёров по реферальному балансу, приглашениям и динамике.</Lead>
      {currentUser && (
        <LeaderHero>
          <LeaderAvatar user={currentUser} size={64} />
          <div>
            <SmallText>Ваше место</SmallText>
            <TitleText>{leaderName(currentUser)}</TitleText>
            <LeaderBadges user={currentUser} />
            <div style={{ marginTop: 10, fontFamily: "Unbounded", fontSize: 20, fontWeight: 900 }}>
              {formatNumberWithSpace(numberOrZero(currentUser.ref_balance))} $
            </div>
          </div>
        </LeaderHero>
      )}
      <SectionTitle>Таблица лидеров</SectionTitle>
      <LeaderList>
        {loading && (
          <RowCard style={{ minHeight: 72, alignItems: "center" }}>
            <TitleText style={{ fontSize: 14 }}>Загружаем лидерборд...</TitleText>
          </RowCard>
        )}
        {!list.length && (
          <RowCard style={{ minHeight: 72, alignItems: "center" }}>
            <div>
              <TitleText style={{ fontSize: 14 }}>{error || "В лидерборде пока нет участников"}</TitleText>
              <SmallText style={{ marginTop: 5 }}>Данные появятся после первых реферальных начислений.</SmallText>
            </div>
          </RowCard>
        )}
        {!loading && list.map((item, index) => (
          <LeaderRow key={String(item.id || index)}>
            <LeaderAvatar user={item} />
            <div>
              <TitleText style={{ fontSize: 13.5 }}>{leaderName(item)}</TitleText>
              <LeaderBadges user={item} />
            </div>
            <div style={{ fontFamily: "Unbounded", fontSize: 13, fontWeight: 900, whiteSpace: "nowrap" }}>
              {formatNumberWithSpace(numberOrZero(item.ref_balance))} $
            </div>
          </LeaderRow>
        ))}
      </LeaderList>
    </Page>
  );
}

function ProfileV2({ balance, setScreen }: { balance: number; setScreen: (screen: Screen) => void }) {
  const [refLink, setRefLink] = useState("<referral link>");
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    getUserRefLink(getUserId()).then((link) => setRefLink(link || "<referral link>"));
  }, []);

  const withdraw = async () => {
    setError("");
    if (!Number(amount) || Number(amount) <= 0) return setError("Введите сумму вывода");
    if (wallet.trim().length < 10) return setError("Введите корректный кошелек");
    try {
      await userWithdraw(getUserId(), Number(amount), wallet.trim(), "TRC20");
      setError("Заявка на вывод создана");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось создать заявку");
    }
  };

  return (
    <Page>
      <H1>Профиль</H1>
      <Lead>Личный кабинет вместо цепочки команд: никнейм TradingView, подписки и быстрые действия.</Lead>
      <Card style={{ marginTop: 22, padding: 18, textAlign: "center", background: "linear-gradient(121deg, rgba(5,7,12,.82), rgba(8,169,255,.36))" }}>
        <SmallText>Реф. баланс</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 28, fontWeight: 900 }}>{formatNumberWithSpace(balance)} $</div>
      </Card>
      <MetricGrid>
        <MetricCard $accent="blue">
          <Icon src={analyticsIcon} $size={44} />
          <SmallText>Рейтинг</SmallText>
          <strong>#832</strong>
        </MetricCard>
        <MetricCard $accent="purple">
          <Icon src={referralsIcon} $size={44} />
          <SmallText>Рефералы</SmallText>
          <strong>24</strong>
        </MetricCard>
      </MetricGrid>
      <LeaderList>
        <PaymentCard icon={supportIcon} title="TradingView nickname" text="Сейчас установлен: DarkPrince2000" onClick={() => setScreen("nickname")} />
        <PaymentCard icon={indicatorIcon} title="Активные подписки" text="Индикатор: осталось 23 дня. Аналитика: осталось 51 день." onClick={() => setScreen("subscriptions")} />
      </LeaderList>
      <ReferralCard>
        <H1 style={{ fontSize: 21, maxWidth: 220 }}>Приглашайте своих друзей в проект!</H1>
        <Lead style={{ maxWidth: 220 }}>Пригласите друга и получайте 10% от всех его затрат в боте!</Lead>
        <img src={saymonDashboard} alt="" style={{ position: "absolute", right: -22, top: 50, width: 154, height: 165, objectFit: "cover", objectPosition: "top" }} />
        <Input readOnly value={refLink} style={{ marginTop: 18, paddingRight: 15 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
          <PrimaryButton onClick={() => shareReferralLink(refLink)}>Поделиться</PrimaryButton>
          <PrimaryButton onClick={() => navigator.clipboard.writeText(refLink)}>Копировать</PrimaryButton>
        </div>
      </ReferralCard>
      <StatsPanel>
        <SmallText style={{ textAlign: "center" }}>Реф. баланс</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 26, fontWeight: 900, textAlign: "center" }}>{formatNumberWithSpace(balance)} $</div>
        <Input placeholder="Сумма вывода" value={amount} onChange={(event) => setAmount(event.target.value)} style={{ marginTop: 14, paddingRight: 15 }} />
        <Input placeholder="Кошелек" value={wallet} onChange={(event) => setWallet(event.target.value)} style={{ marginTop: 8, paddingRight: 15 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
          <GhostButton>USDT TRC 20</GhostButton>
          <GhostButton>USDT ERC 20</GhostButton>
        </div>
        <PrimaryButton style={{ marginTop: 10 }} onClick={withdraw}>Вывести</PrimaryButton>
      </StatsPanel>
      {error && <ErrorLine>{error}</ErrorLine>}
    </Page>
  );
}

function StatsV2() {
  const conversion = 68;
  return (
    <Page $short>
      <H1>Статы</H1>
      <Lead>Краткая аналитика по рефералам, покупкам и продлениям подписок.</Lead>
      <MetricGrid>
        <MetricCard $accent="blue">
          <Icon src={referralsIcon} $size={42} />
          <SmallText>Рефералы</SmallText>
          <strong>24</strong>
        </MetricCard>
        <MetricCard $accent="green">
          <Icon src={walletIcon} $size={42} />
          <SmallText>Заработано</SmallText>
          <strong>2 579 $</strong>
        </MetricCard>
        <MetricCard $accent="yellow">
          <Icon src={indicatorIcon} $size={42} />
          <SmallText>Купили подписку</SmallText>
          <strong>24</strong>
        </MetricCard>
        <MetricCard $accent="purple">
          <Icon src={analyticsIcon} $size={42} />
          <SmallText>Продления</SmallText>
          <strong>3</strong>
        </MetricCard>
      </MetricGrid>
      <StatsPanel>
        <TitleText>Конверсия</TitleText>
        <SummaryRow><span>С подпиской</span><strong>{conversion}%</strong></SummaryRow>
        <ProgressTrack><ProgressFill $value={conversion} /></ProgressTrack>
        <SummaryRow><span>Без покупки</span><strong>{100 - conversion}%</strong></SummaryRow>
        <ProgressTrack><ProgressFill $value={100 - conversion} $tone="purple" /></ProgressTrack>
      </StatsPanel>
      <StatsPanel>
        <TitleText>По продуктам</TitleText>
        <SummaryRow><span>Индикатор</span><strong>18</strong></SummaryRow>
        <SummaryRow><span>Аналитика</span><strong>9</strong></SummaryRow>
        <SummaryRow><span>Обучение</span><strong>4</strong></SummaryRow>
      </StatsPanel>
    </Page>
  );
}

function DesignLeaderBadges({ position, invited = 0, change = 0 }: { position?: number; invited?: number; change?: number }) {
  const safePosition = numberOrZero(position);
  const safeInvited = numberOrZero(invited);
  const safeChange = numberOrZero(change);
  const isUp = safeChange >= 0;
  const changeLabel = `${isUp ? "▲" : "▼"} ${Math.abs(safeChange)}`;
  const pillStyle = {
    minWidth: "clamp(36px, 10.8vw, 51px)",
    height: "clamp(22px, 6.6vw, 31px)",
    borderRadius: "clamp(11px, 3.3vw, 16px)",
    fontSize: "clamp(10px, 3vw, 14px)",
  };

  return (
    <BadgeRow style={{ gap: "clamp(5px, 1.5vw, 7px)", marginTop: "clamp(7px, 2.1vw, 10px)", alignItems: "center" }}>
      <Pill style={pillStyle}>{safePosition > 0 ? `#${safePosition}` : "#-"}</Pill>
      <Pill style={pillStyle}>{safeInvited}</Pill>
      <Pill style={pillStyle} $tone={isUp ? "green" : "dark"}>{changeLabel}</Pill>
    </BadgeRow>
  );
}

function LeadersDesign() {
  const [users, setUsers] = useState<IUser[]>([]);
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    const userId = getUserId();

    setLoading(true);
    setError("");
    Promise.all([
      getTopUsers(1),
      userId > 0 ? getUserLiderboardByTg(userId).catch(() => null) : Promise.resolve(null),
    ])
      .then(([leaders, currentUser]) => {
        if (!isMounted) return;
        setUsers(Array.isArray(leaders.users) ? leaders.users : []);
        setUser(currentUser);
      })
      .catch(() => {
        if (!isMounted) return;
        setUsers([]);
        setUser(null);
        setError("Лидерборд пока недоступен");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const list = users.slice(0, 5);
  const currentUser = user || list[0] || null;
  const heroImage = currentUser?.image || list.find((item) => item.image)?.image || "";

  if (loading) {
    return (
      <DataLoadingOverlay>
        <LoadingPage />
      </DataLoadingOverlay>
    );
  }

  return (
    <DesignPage $flush $leaders>
      <LeaderBoard>
        <LeaderTop>
          <H1 className="leader-title">Таблица лидеров</H1>
          <HexAvatar $large $src={heroImage} />
          <div style={{ position: "relative", zIndex: 2, minWidth: 0 }}>
            <TitleText style={{ fontSize: "clamp(18px, 5.4vw, 25px)", lineHeight: "1.22", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {leaderName(currentUser)}
            </TitleText>
            <DesignLeaderBadges
              position={leaderPosition(currentUser)}
              invited={numberOrZero(currentUser?.invited_count)}
              change={numberOrZero(currentUser?.change)}
            />
          </div>
          <img className="robot" src={leaderRobot} alt="" />
        </LeaderTop>
        {loading && (
          <LeaderTableRow>
            <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <TitleText style={{ fontSize: "clamp(13px, 3.9vw, 18px)" }}>Загружаем лидерборд...</TitleText>
              <SmallText style={{ marginTop: "clamp(5px, 1.5vw, 7px)" }}>Получаем актуальный топ рефоводов.</SmallText>
            </div>
          </LeaderTableRow>
        )}
        {!loading && !list.length && (
          <LeaderTableRow>
            <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <TitleText style={{ fontSize: "clamp(13px, 3.9vw, 18px)" }}>{error || "В лидерборде пока нет участников"}</TitleText>
              <SmallText style={{ marginTop: "clamp(5px, 1.5vw, 7px)" }}>Данные появятся после первых реферальных начислений.</SmallText>
            </div>
          </LeaderTableRow>
        )}
        {list.map((item, index) => {
          const position = leaderPosition(item, index + 1);
          const invited = numberOrZero(item.invited_count);
          const change = numberOrZero(item.change);
          const balance = numberOrZero(item.ref_balance);

          return (
          <LeaderTableRow key={String(item.id || index)}>
            {item.image || heroImage ? (
              <Avatar src={item.image || heroImage} $size={54} style={{ width: "clamp(54px, 16.2vw, 76px)", height: "clamp(54px, 16.2vw, 76px)", borderRadius: 8 }} />
            ) : (
              <Avatar as="div" $size={54} style={{ width: "clamp(54px, 16.2vw, 76px)", height: "clamp(54px, 16.2vw, 76px)", borderRadius: 8 }} />
            )}
            <div style={{ minWidth: 0 }}>
              <TitleText style={{ maxWidth: "clamp(106px, 31.8vw, 150px)", fontSize: "clamp(13px, 3.9vw, 18px)", lineHeight: "1.28", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {leaderName(item)}
              </TitleText>
              <DesignLeaderBadges position={position} invited={invited} change={change} />
            </div>
            <div style={{ position: "absolute", right: "clamp(22px, 6.6vw, 31px)", top: "clamp(24px, 7.2vw, 34px)", fontSize: "clamp(13px, 3.9vw, 18px)", fontWeight: 700, whiteSpace: "nowrap" }}>
              {formatNumberWithSpace(balance)} $
            </div>
          </LeaderTableRow>
          );
        })}
      </LeaderBoard>
    </DesignPage>
  );
}

function ProfileDesign({ balance, setScreen }: { balance: number; setScreen: (screen: Screen) => void }) {
  const [refLink, setRefLink] = useState("<referral link>");
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    getUserRefLink(getUserId()).then((link) => setRefLink(link || "<referral link>"));
  }, []);

  const withdraw = async () => {
    setError("");
    if (!Number(amount) || Number(amount) <= 0) return setError("Введите сумму вывода");
    if (wallet.trim().length < 10) return setError("Введите корректный кошелек");
    try {
      await userWithdraw(getUserId(), Number(amount), wallet.trim(), "TRC20");
      setError("Заявка на вывод создана");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось создать заявку");
    }
  };

  return (
    <DesignPage>
      <H1 style={{ fontSize: 20 }}>Профиль</H1>
      <Lead style={{ fontSize: 9.5, maxWidth: 228, marginTop: 6 }}>
        Личный кабинет вместо цепочки команд: никнейм TradingView, подписки и быстрые действия.
      </Lead>
      <Card style={{ marginTop: 12, padding: 8, minHeight: 48, borderRadius: 6, textAlign: "center", background: "linear-gradient(100deg, rgba(50,50,56,.92), rgba(53,125,190,.74))" }}>
        <SmallText style={{ marginTop: 0 }}>Реф. баланс</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 20, fontWeight: 900 }}>{formatNumberWithSpace(balance)} $</div>
      </Card>
      <MetricGrid style={{ gap: 12, marginTop: 12 }}>
        <MetricCard $accent="blue" style={{ minHeight: 74, borderRadius: 6, padding: 9 }}>
          <Icon src={analyticsIcon} $size={32} />
          <SmallText>Рейтинг</SmallText>
          <strong style={{ fontSize: 14, marginTop: 3 }}>#832</strong>
        </MetricCard>
        <MetricCard $accent="purple" style={{ minHeight: 74, borderRadius: 6, padding: 9 }}>
          <Icon src={referralsIcon} $size={32} />
          <SmallText>Рефералы</SmallText>
          <strong style={{ fontSize: 14, marginTop: 3 }}>24</strong>
        </MetricCard>
      </MetricGrid>
      <LeaderList style={{ gap: 8, marginTop: 10 }}>
        <RowCard onClick={() => setScreen("nickname")} style={{ minHeight: 52, borderRadius: 6, padding: "8px 10px", gridTemplateColumns: "38px 1fr auto" }}>
          <Icon src={supportIcon} $size={38} />
          <div>
            <TitleText style={{ fontSize: 11 }}>TradingView nickname</TitleText>
            <SmallText style={{ fontSize: 7.8 }}>Сейчас установлен: DarkPrince2000</SmallText>
          </div>
          <span style={{ fontSize: 18 }}>›</span>
        </RowCard>
        <RowCard onClick={() => setScreen("subscriptions")} style={{ minHeight: 58, borderRadius: 6, padding: "8px 10px", gridTemplateColumns: "38px 1fr auto" }}>
          <Icon src={indicatorIcon} $size={38} />
          <div>
            <TitleText style={{ fontSize: 11 }}>Активные подписки</TitleText>
            <SmallText style={{ fontSize: 7.8 }}>Индикатор: осталось 23 дня. Аналитика: осталось 51 день.</SmallText>
          </div>
          <span style={{ fontSize: 18 }}>›</span>
        </RowCard>
      </LeaderList>
      <ReferralCard style={{ marginTop: 12, minHeight: 150, padding: 10, borderRadius: 6 }}>
        <H1 style={{ fontSize: 17, maxWidth: 164 }}>Приглашайте своих друзей в проект!</H1>
        <Lead style={{ maxWidth: 158, fontSize: 9, marginTop: 6 }}>Пригласите друга и получайте 10% от всех его затрат в боте!</Lead>
        <img src={saymonDashboard} alt="" style={{ position: "absolute", right: -16, top: -10, width: 122, height: 136, objectFit: "cover", objectPosition: "top" }} />
        <Input readOnly value={refLink} style={{ height: 24, borderRadius: 6, marginTop: 8, paddingRight: 10, paddingLeft: 10, fontSize: 9 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
          <PrimaryButton style={{ height: 32, borderRadius: 6, fontSize: 10 }} onClick={() => shareReferralLink(refLink)}>Поделиться</PrimaryButton>
          <PrimaryButton style={{ height: 32, borderRadius: 6, fontSize: 10 }} onClick={() => navigator.clipboard.writeText(refLink)}>Копировать</PrimaryButton>
        </div>
      </ReferralCard>
      <StatsPanel style={{ marginTop: 8, padding: 8, borderRadius: 6, textAlign: "center", background: "linear-gradient(100deg, rgba(50,50,56,.92), rgba(45,72,91,.78))" }}>
        <SmallText style={{ marginTop: 0 }}>Реф. баланс</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 20, fontWeight: 900 }}>{formatNumberWithSpace(balance)} $</div>
      </StatsPanel>
      <Input placeholder="Сумма вывода" value={amount} onChange={(event) => setAmount(event.target.value)} style={{ height: 27, borderRadius: 6, marginTop: 7, paddingRight: 10, paddingLeft: 10, fontSize: 10 }} />
      <Input placeholder="Кошелек" value={wallet} onChange={(event) => setWallet(event.target.value)} style={{ height: 27, borderRadius: 6, marginTop: 5, paddingRight: 10, paddingLeft: 10, fontSize: 10 }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
        <GhostButton style={{ minHeight: 26, borderRadius: 6, fontSize: 9 }}>USDT TRC 20</GhostButton>
        <GhostButton style={{ minHeight: 26, borderRadius: 6, fontSize: 9 }}>USDT ERC 20</GhostButton>
      </div>
      <PrimaryButton style={{ height: 30, borderRadius: 6, marginTop: 5, fontSize: 10 }} onClick={withdraw}>Вывести</PrimaryButton>
      {error && <ErrorLine>{error}</ErrorLine>}
    </DesignPage>
  );
}

function ProfileDesign2({ balance, setScreen }: { balance: number; setScreen: (screen: Screen) => void }) {
  const [refLink, setRefLink] = useState("<referral link>");
  const [profile, setProfile] = useState<IUserProfile | null>(null);
  const [leaderProfile, setLeaderProfile] = useState<IUser | null>(null);
  const [subscriptions, setSubscriptions] = useState<IUserActiveSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [walletType, setWalletType] = useState<"TRC20" | "ERC20">("TRC20");
  const [amountError, setAmountError] = useState("");
  const [walletError, setWalletError] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [copyNotice, setCopyNotice] = useState("");

  useEffect(() => {
    let isMounted = true;
    const userId = getUserId();

    setLoading(true);
    Promise.allSettled([
      withTimeout(getUserProfile(userId)),
      withTimeout(getUserLiderboardByTg(userId)),
      withTimeout(getUserRefLink(userId)),
      withTimeout(getUserActiveSubscriptions(userId)),
    ])
      .then(([profileResult, leaderResult, linkResult, activeSubscriptionsResult]) => {
        if (!isMounted) return;

        setProfile(profileResult.status === "fulfilled" ? profileResult.value : null);
        setLeaderProfile(leaderResult.status === "fulfilled" ? leaderResult.value : null);
        setRefLink(linkResult.status === "fulfilled" && linkResult.value ? linkResult.value : "<referral link>");
        setSubscriptions(activeSubscriptionsResult.status === "fulfilled" ? activeSubscriptionsResult.value : []);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const refBalance = numberOrZero(profile?.ref_balance ?? balance);
  const ranking = numberOrZero(profile?.position || leaderProfile?.position);
  const referrals = numberOrZero(profile?.invited_count || leaderProfile?.invited_count);
  const nickname = profile?.trading_view_name?.trim();
  const subscriptionsPreview = subscriptions.length
    ? subscriptions
        .map((item) => item.kind === "study" ? item.name : `${item.name}: осталось ${numberOrZero(item.days_remaining)} дн.`)
        .join("\n")
    : "Нет активных подписок";

  const withdraw = async () => {
    setAmountError("");
    setWalletError("");
    setFormMessage("");
    const amountNumber = Number(amount);
    if (!amountNumber || amountNumber <= 0) {
      setAmountError("Введите сумму вывода");
      return;
    }
    if (wallet.trim().length < 10) {
      setWalletError("Введите корректный кошелек");
      return;
    }

    try {
      await userWithdraw(getUserId(), amountNumber, wallet.trim(), walletType);
      setProfile((current) =>
          current ? { ...current, ref_balance: Math.max(0, refBalance - amountNumber) } : current
      );
      setFormMessage("Заявка на вывод создана");
      setAmount("");
      setWallet("");
    } catch (err) {
      setFormMessage(err instanceof Error ? err.message : "Не удалось создать заявку");
    }
  };

  const copyRefLink = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      setCopyNotice("Скопировано успешно");
      window.setTimeout(() => setCopyNotice(""), 2200);
    } catch {
      setCopyNotice("Не удалось скопировать ссылку");
      window.setTimeout(() => setCopyNotice(""), 2200);
    }
  };

  const profileBlockStyle = { width: "100%", boxSizing: "border-box" as const };

  if (loading) {
    return (
      <DataLoadingOverlay>
        <LoadingPage />
      </DataLoadingOverlay>
    );
  }

  return (
    <DesignPage>
      <H1 style={{ fontSize: 32, lineHeight: 1.05 }}>Профиль</H1>
      <Lead style={{ fontSize: 16, maxWidth: 390, marginTop: 12, lineHeight: 1.28 }}>
        Личный кабинет вместо цепочки команд: никнейм TradingView, подписки и быстрые действия.
      </Lead>

      <Card style={{ ...profileBlockStyle, marginTop: 28, padding: 18, minHeight: 116, borderRadius: 16, border: "none", textAlign: "center", background: "linear-gradient(100deg, rgba(50,50,56,.92), rgba(53,125,190,.74))" }}>
        <SmallText style={{ marginTop: 0, fontSize: 20 }}>Реф. баланс</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 44, lineHeight: 1.1, fontWeight: 900 }}>{formatNumberWithSpace(refBalance)} $</div>
      </Card>

      <MetricGrid style={{ ...profileBlockStyle, gap: 24, marginTop: 16 }}>
        <MetricCard $accent="blue" style={{ minHeight: 150, borderRadius: 16, padding: 20, overflow: "hidden" }}>
          <Icon src={analyticsIcon} $size={62} />
          <SmallText style={{ fontSize: 16, marginTop: 14 }}>Рейтинг</SmallText>
          <strong style={{ fontSize: 28, marginTop: 4 }}>{ranking > 0 ? `#${ranking}` : "#-"}</strong>
        </MetricCard>
        <MetricCard $accent="purple" style={{ minHeight: 150, borderRadius: 16, padding: 20, overflow: "hidden" }}>
          <Icon src={referralsIcon} $size={62} />
          <SmallText style={{ fontSize: 16, marginTop: 14 }}>Рефералы</SmallText>
          <strong style={{ fontSize: 28, marginTop: 4 }}>{referrals}</strong>
        </MetricCard>
      </MetricGrid>

      <LeaderList style={{ ...profileBlockStyle, gap: 12, marginTop: 16 }}>
        <RowCard onClick={() => setScreen("nickname")} style={{ minHeight: 98, borderRadius: 16, padding: "16px 20px", gridTemplateColumns: "66px 1fr auto" }}>
          <Icon src={supportIcon} $size={62} />
          <div>
            <TitleText style={{ fontSize: 18 }}>TradingView nickname</TitleText>
            <SmallText style={{ fontSize: 15, marginTop: 7 }}>
              {nickname ? `Сейчас установлен: ${nickname}` : "Никнейм еще не указан"}
            </SmallText>
          </div>
          <span style={{ fontSize: 34 }}>›</span>
        </RowCard>
        {subscriptions.length > 0 && (
          <RowCard onClick={() => setScreen("subscriptions")} style={{ minHeight: 110, borderRadius: 16, padding: "16px 20px", gridTemplateColumns: "66px 1fr auto" }}>
            <Icon src={indicatorIcon} $size={62} />
            <div>
              <TitleText style={{ fontSize: 18 }}>Активные подписки</TitleText>
              <SmallText style={{ fontSize: 15, marginTop: 7, whiteSpace: "pre-line" }}>{subscriptionsPreview}</SmallText>
            </div>
            <span style={{ fontSize: 34 }}>›</span>
          </RowCard>
        )}
      </LeaderList>

      <ReferralCard style={{ ...profileBlockStyle, marginTop: 18, minHeight: 308, padding: "18px 14px 0", border: "none", borderRadius: 0, overflow: "visible", background: "transparent", backdropFilter: "none" }}>
        <H1 style={{ position: "relative", zIndex: 2, fontSize: 28, lineHeight: 1.12, maxWidth: 272 }}>Приглашайте своих друзей в проект!</H1>
        <Lead style={{ position: "relative", zIndex: 2, maxWidth: 290, fontSize: 16, marginTop: 12, lineHeight: 1.22, color: "#fff" }}>
          Пригласите друга и получайте 10% от всех его затрат в боте!
        </Lead>
        <ProfileReferralRobot src={leaderRobot} alt="" />
        <Input readOnly value={refLink} style={{ position: "relative", zIndex: 3, height: 56, borderRadius: 12, marginTop: 22, paddingRight: 16, paddingLeft: 16, fontSize: 16, background: "rgba(5, 15, 29, 0.72)" }} />
        <div style={{ position: "relative", zIndex: 3, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
          <PrimaryButton style={{ height: 60, borderRadius: 12, fontSize: 18 }} onClick={() => shareReferralLink(refLink)}>Поделиться</PrimaryButton>
          <PrimaryButton style={{ height: 60, borderRadius: 12, fontSize: 18 }} onClick={copyRefLink}>Копировать</PrimaryButton>
        </div>
        {copyNotice && (
          <CopyNotice $success={copyNotice.includes("успешно")} style={{ marginTop: 12 }}>
            {copyNotice}
          </CopyNotice>
        )}
      </ReferralCard>

      <StatsPanel style={{ ...profileBlockStyle, marginTop: 18, padding: 18, minHeight: 110, border: "none", borderRadius: 16, textAlign: "center", background: "linear-gradient(100deg, rgba(50,50,56,.92), rgba(45,72,91,.78))" }}>
        <SmallText style={{ marginTop: 0, fontSize: 20 }}>Реф. баланс</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 44, lineHeight: 1.1, fontWeight: 900 }}>{formatNumberWithSpace(refBalance)} $</div>
      </StatsPanel>
      <Input $error={Boolean(amountError)} placeholder="Сумма вывода" value={amount} onChange={(event) => { setAmount(event.target.value); setAmountError(""); }} style={{ height: 56, borderRadius: 12, marginTop: 10, paddingRight: 18, paddingLeft: 18, fontSize: 18 }} />
      {amountError && <ErrorLine>{amountError}</ErrorLine>}
      <Input $error={Boolean(walletError)} placeholder="Кошелек" value={wallet} onChange={(event) => { setWallet(event.target.value); setWalletError(""); }} style={{ height: 56, borderRadius: 12, marginTop: 8, paddingRight: 18, paddingLeft: 18, fontSize: 18 }} />
      {walletError && <ErrorLine>{walletError}</ErrorLine>}
      <div style={{ ...profileBlockStyle, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 8 }}>
        <GhostButton style={{ minHeight: 52, borderRadius: 12, fontSize: 16, background: walletType === "TRC20" ? "rgba(8,169,255,.32)" : undefined }} onClick={() => setWalletType("TRC20")}>USDT TRC 20</GhostButton>
        <GhostButton style={{ minHeight: 52, borderRadius: 12, fontSize: 16, background: walletType === "ERC20" ? "rgba(8,169,255,.32)" : undefined }} onClick={() => setWalletType("ERC20")}>USDT ERC 20</GhostButton>
      </div>
      <PrimaryButton style={{ height: 60, borderRadius: 12, marginTop: 18, fontSize: 18 }} onClick={withdraw}>Вывести</PrimaryButton>
      {formMessage && <ErrorLine style={{ color: formMessage.includes("создана") ? COLORS.green : COLORS.danger }}>{formMessage}</ErrorLine>}
      <div style={{ height: 42 }} />
    </DesignPage>
  );
}

function StatsDesign() {
  const [stats, setStats] = useState<IUserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    const userId = getUserId();

    if (userId <= 0) {
      setError("Не удалось определить пользователя");
      setLoading(false);
      return;
    }

    getUserStats(userId)
      .then((data) => {
        if (isMounted) setStats(data);
      })
      .catch(() => {
        if (isMounted) setError("Статистика пока недоступна");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <DataLoadingOverlay>
        <LoadingPage />
      </DataLoadingOverlay>
    );
  }

  const totalInvited = numberOrZero(stats?.total_invited_users);
  const subscriptionsBought = numberOrZero(stats?.subscriptions_bought);
  const percentage = totalInvited ? Math.min(100, Math.round((subscriptionsBought / totalInvited) * 100)) : 0;
  const tariffEntries = Object.entries(stats?.subscriptions_by_tariff || {});

  return (
    <DesignPage>
      <H1 style={{ fontSize: 28, lineHeight: 1.08, maxWidth: 360 }}>Статистика реферальной ссылки</H1>
      <SectionTitle style={{ fontSize: 19, margin: "22px 0 8px" }}>Заработок</SectionTitle>
      <Lead style={{ fontSize: 14, lineHeight: 1.3, maxWidth: 420, marginTop: 0 }}>
        Здесь показано сколько ты заработал за всё время работы с нами, и сколько вывел денежных средств.
      </Lead>
      {error && <ErrorLine style={{ marginTop: 14 }}>{error}</ErrorLine>}
      <DesignStatCard style={{ marginTop: 14 }}>
        <SmallText style={{ marginTop: 0, fontSize: 14 }}>Заработано всего</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 30, fontWeight: 900, textAlign: "center" }}>{formatNumberWithSpace(numberOrZero(stats?.earned))} $</div>
      </DesignStatCard>
      <DesignStatCard style={{ marginTop: 12 }}>
        <SmallText style={{ marginTop: 0, fontSize: 14 }}>Выведено</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 30, fontWeight: 900, textAlign: "center" }}>{formatNumberWithSpace(numberOrZero(stats?.withdrawn))} $</div>
      </DesignStatCard>
      <SectionTitle style={{ fontSize: 19, margin: "22px 0 8px" }}>Статистика покупок</SectionTitle>
      <Lead style={{ fontSize: 14, lineHeight: 1.3, marginTop: 0 }}>
        Здесь показан процент успешных покупок по вашей реферальной ссылке, а так же какие именно тарифы покупали клиенты.
      </Lead>
      <StatsPanel style={{ marginTop: 14, padding: "18px 16px 14px", borderRadius: 14, background: "linear-gradient(120deg, rgba(63,93,118,.82), rgba(58,57,62,.9))" }}>
        <Donut $percentage={percentage}>
          <div>
            <div style={{ fontFamily: "Unbounded", fontSize: 34, fontWeight: 900 }}>{percentage}%</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{subscriptionsBought} из {totalInvited}</div>
          </div>
        </Donut>
        <div style={{ display: "flex", justifyContent: "center", gap: 14, fontSize: 10, whiteSpace: "nowrap" }}>
          <span><b style={{ color: COLORS.blue }}>●</b> Купили подписку</span>
          <span><b style={{ color: "#e51d24" }}>●</b> Не купили подписку</span>
        </div>
      </StatsPanel>
      {tariffEntries.map(([label, value]) => (
        <DesignStatCard key={label} style={{ marginTop: 12 }}>
          <SmallText style={{ marginTop: 0, fontSize: 13 }}>{label}</SmallText>
          <div style={{ fontFamily: "Unbounded", fontSize: 27, fontWeight: 900, textAlign: "center" }}>{formatNumberWithSpace(numberOrZero(value))}</div>
        </DesignStatCard>
      ))}
      <DesignStatCard style={{ marginTop: 12 }}>
        <SmallText style={{ marginTop: 0, fontSize: 13 }}>Продлили подписку</SmallText>
        <div style={{ fontFamily: "Unbounded", fontSize: 27, fontWeight: 900, textAlign: "center" }}>{formatNumberWithSpace(numberOrZero(stats?.subscription_continiued))}</div>
      </DesignStatCard>
      <div style={{ height: 24 }} />
    </DesignPage>
  );
}

export default function MiniApp() {
  const [screen, setScreen, transitionLoading] = useScreen();
  const orderStorageKey = `saymon.mini.order.${getUserId() || "telegram"}`;
  const savedOrder = useMemo(() => {
    try {
      const value = JSON.parse(localStorage.getItem(orderStorageKey) || "null");
      return value && typeof value === "object" ? value : {};
    } catch {
      return {};
    }
  }, [orderStorageKey]);
  const [selectedIds, setSelectedIds] = useState<string[]>(() => Array.isArray(savedOrder.selectedIds) ? savedOrder.selectedIds : []);
  const [catalogFilter, setCatalogFilter] = useState<"subscription" | "study">(() => savedOrder.catalogFilter === "study" ? "study" : "subscription");
  const [duration, setDuration] = useState<number | null>(() => {
    const saved = Number(savedOrder.duration);
    return Number.isFinite(saved) && saved > 0 ? saved : null;
  });
  const [balance, setBalance] = useState(0);
  const [bootLoading, setBootLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>(fallbackProducts);
  const [discounts, setDiscounts] = useState<MiniAppDiscounts>(defaultDiscounts);
  const [glowStep, setGlowStep] = useState(() => getGlowStep(screen));

  useEffect(() => {
    localStorage.setItem(orderStorageKey, JSON.stringify({ selectedIds, catalogFilter, duration }));
  }, [catalogFilter, duration, orderStorageKey, selectedIds]);

  useEffect(() => {
    if (bootLoading || transitionLoading) return;
    const timer = window.setTimeout(() => setGlowStep(getGlowStep(screen)), 120);
    return () => window.clearTimeout(timer);
  }, [bootLoading, screen, transitionLoading]);

  useEffect(() => {
    let isMounted = true;
    const id = getUserId();
    if (id > 0) localStorage.setItem("userId", String(id));
    const load = async () => {
      const [subscriptionTariffs, studyTariffs, discountConfig, userBalance, activeSubscriptions] = await Promise.all([
        withTimeout(getTariffsReq("subscription")),
        withTimeout(getTariffsReq("study")),
        withTimeout(getMiniAppDiscountsReq()),
        withTimeout(getUserBalance(id)).catch(() => 0),
        withTimeout(getUserActiveSubscriptions(id)).catch(() => []),
        preloadMiniAppAssets(),
        new Promise<void>((resolve) => window.setTimeout(resolve, 900)),
      ]);
      const tariffs = [...subscriptionTariffs, ...studyTariffs];
      const dynamicProducts = tariffs
        .map(tariffToProduct)
        .filter((product) => {
          if (product.kind !== "study") return true;
          if (!Array.isArray(activeSubscriptions)) return true;
          const activeStudyNames = new Set(
            activeSubscriptions
              .filter((item) => item.kind === "study")
              .map((item) => normalizeProductName(item.name)),
          );
          return !activeStudyNames.has(normalizeProductName(product.title));
        });
      await Promise.all(dynamicProducts.slice(0, 4).map((product) => preloadImage(product.icon)));
      if (!isMounted) return;
      if (dynamicProducts.length) setProducts(dynamicProducts);
      if (discountConfig) setDiscounts(discountConfig);
      setBalance(userBalance);
      setBootLoading(false);
    };
    const fallbackTimer = window.setTimeout(() => {
      if (isMounted) setBootLoading(false);
    }, 10000);
    void load().catch(() => {
      if (isMounted) setBootLoading(false);
    });
    return () => {
      isMounted = false;
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  const selectedProducts = products.filter((product) => selectedIds.includes(product.id));
  const safeSelected = selectedProducts.length ? selectedProducts : products.slice(0, 1);

  return (
    <>
      <MiniAppGlobalStyle />
      <Shell $glowStep={glowStep}>
        {!bootLoading && (
          <ScreenStage key={screen}>
            {screen === "home" && <Dashboard balance={balance} products={products} setSelectedIds={setSelectedIds} setCatalogFilter={setCatalogFilter} setScreen={setScreen} />}
            {screen === "catalog" && (
              <Catalog
                products={products}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                setScreen={setScreen}
                initialFilter={catalogFilter}
                setCatalogFilter={setCatalogFilter}
              />
            )}
            {screen === "config" && <Configurator selectedProducts={safeSelected} discounts={discounts} duration={duration} setDuration={setDuration} setScreen={setScreen} />}
            {screen === "checkout" && <Checkout selectedProducts={safeSelected} discounts={discounts} duration={duration} balance={balance} setScreen={setScreen} />}
            {screen === "waiting" && <StatusScreen kind="waiting" setScreen={setScreen} />}
            {screen === "paid" && <StatusScreen kind="paid" setScreen={setScreen} />}
            {screen === "manual" && <StatusScreen kind="manual" setScreen={setScreen} />}
            {screen === "manual-payeer" && <StatusScreen kind="manual-payeer" selectedProducts={safeSelected} discounts={discounts} duration={duration} setScreen={setScreen} />}
            {screen === "manual-submitted" && <StatusScreen kind="manual-submitted" selectedProducts={safeSelected} discounts={discounts} duration={duration} setScreen={setScreen} />}
            {screen === "profile" && <ProfileDesign2 balance={balance} setScreen={setScreen} />}
            {screen === "subscriptions" && <ActiveSubscriptions />}
            {screen === "nickname" && <Nickname setScreen={setScreen} />}
            {screen === "free" && <FreeChannel success={false} setScreen={setScreen} />}
            {screen === "free-success" && <FreeChannel success setScreen={setScreen} />}
            {screen === "leaders" && <LeadersDesign />}
            {screen === "stats" && <StatsDesign />}
            {screen === "support" && <SupportScreen />}
          </ScreenStage>
        )}
        {!bootLoading && <Navigation active={screen} setScreen={setScreen} />}
        {(bootLoading || transitionLoading) && <DataLoadingOverlay><LoadingPage /></DataLoadingOverlay>}
      </Shell>
    </>
  );
}
